import {FC, useCallback, useEffect, useState} from 'react';
import {Header} from '@components/Header';
import {Nav} from '@components/Nav';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {Logo} from '@components/Logo';
import {ButtonGroup} from '@components/ButtonGroup';
import {Button} from '@components/Button';
import {Typography} from '@components/Typography';
import {Icon} from '@components/Icon';
import {FormattedMessage} from 'react-intl';
import Link from 'next/link';
import NextImage from 'next/image';
import {getLocalizedRoute, isRoute, routes} from '@utils/routes';
import {useSession} from 'next-auth/react';
import {Avatar} from '@components/Avatar';
import {SidebarLogin} from './SidebarLogin';
import {useUserNotificationCountsLazyQuery} from '@gql/query/user/counts.gql';
import {ButtonBadge} from '@components/ButtonBadge';
import {Badge} from '@components/Badge';
// import {InteractionLoadable} from '@liveComponents/Loadable';
import dynamic from 'next/dynamic';
import {useLocale} from '@utils/locale';
import {useRouter} from 'next/router';
import {SidebarMenu} from './SidebarMenu';
import {fullName} from '@pageComponents/MyBezrealitky/utils';
import {RentMenu} from './RentMenu';
import {SellMenu} from './SellMenu';
import {LocaleEnum} from 'src/types/general';
import {useUser} from '@utils/user';

const SidebarMenuUser = dynamic(() => import('./SidebarMenuUser'), {ssr: false});

type PageHeaderProps = {
    withHeaderBorder?: boolean;
};

const onHide = (e?: React.MouseEvent<HTMLElement>) => {
    e?.currentTarget.blur();
};

export const PageHeader: FC<PageHeaderProps> = ({withHeaderBorder}) => {
    const locale = useLocale();
    const router = useRouter();
    const {user} = useUser();

    const [getCounts, countData] = useUserNotificationCountsLazyQuery({
        fetchPolicy: 'network-only',
        pollInterval: 300000,
    });

    const [showMenuSidebar, setMenuSidebar] = useState<boolean>(false);
    const [showLoginSidebar, setLoginSidebar] = useState<boolean>(false);

    const closeMenuSidebar = useCallback(() => {
        setMenuSidebar(false);
    }, []);

    const openMenuSidebar = useCallback(() => {
        setMenuSidebar(true);
        setLoginSidebar(false);
    }, []);

    const closeLoginSidebar = useCallback(() => {
        setLoginSidebar(false);
    }, []);

    const openLoginSidebar = useCallback(() => {
        setLoginSidebar(true);
        setMenuSidebar(false);
    }, []);

    useEffect(() => {
        if (user) {
            getCounts({
                variables: {
                    user: user.id,
                },
            });
        }
    }, [user, getCounts]);

    const userMenuButton = (
        <Header.Button onClick={showLoginSidebar ? closeLoginSidebar : openLoginSidebar}>
            {!!user ? (
                user.photo ? (
                    <>
                        <VisuallyHidden>
                            <FormattedMessage defaultMessage="Uživatelský profil" />
                        </VisuallyHidden>

                        {showLoginSidebar ? (
                            <Icon.Close position="middle" />
                        ) : (
                            <Avatar size="xs">
                                <NextImage src={user.photo} width={28} height={28} alt={fullName(user.firstname, user.lastname)} />
                            </Avatar>
                        )}
                    </>
                ) : (
                    <Avatar iconName={showLoginSidebar ? 'Close' : 'Profile'} size="xxs" />
                )
            ) : (
                <Icon name={showLoginSidebar ? 'Close' : 'Profile'} position="middle" />
            )}
        </Header.Button>
    );

    return (
        <>
            <Header withBorder={withHeaderBorder}>
                <Header.Toolbar placement="left" className="d-md-none">
                    <ButtonGroup>
                        <Header.Button onClick={showMenuSidebar ? closeMenuSidebar : openMenuSidebar}>
                            <VisuallyHidden>
                                <FormattedMessage defaultMessage="Hlavní menu" />
                            </VisuallyHidden>
                            <Icon name={showMenuSidebar ? 'Close' : 'Menu'} position="middle" />
                        </Header.Button>
                    </ButtonGroup>
                </Header.Toolbar>

                <Header.Logo>
                    <Link href={getLocalizedRoute(routes.homepage, locale)}>
                        <a>
                            <VisuallyHidden>Bezrealitky</VisuallyHidden>
                            <Logo />
                        </a>
                    </Link>
                </Header.Logo>

                <Header.Nav>
                    <Nav as="ul">
                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.list, locale, {list: ''})} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.list, locale)}>
                                    <FormattedMessage defaultMessage="Nemovitosti" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentreIntroCustomer, locale)} passHref>
                                <Nav.Link>
                                    <FormattedMessage defaultMessage="Tipy pro zájemce" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentrePremium, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.serviceCentrePremium, locale)}>
                                    <FormattedMessage defaultMessage="Premium profil" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.serviceCentreSale, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.serviceCentreSale, locale)} onClick={onHide}>
                                    <FormattedMessage defaultMessage="Služby" />
                                </Nav.Link>
                            </Link>

                            <Header.NavDropdown>
                                <Nav as="ul">
                                    <Nav.Item as="li">
                                        <Link href={getLocalizedRoute(routes.serviceCentreRent, locale)} passHref>
                                            <Nav.Link active={isRoute(router.asPath, routes.serviceCentreRent, locale)} onClick={onHide}>
                                                <FormattedMessage defaultMessage="Pronájem" />
                                            </Nav.Link>
                                        </Link>

                                        <Header.NavDropdown>
                                            <RentMenu onHide={onHide} />
                                        </Header.NavDropdown>
                                    </Nav.Item>
                                    <Nav.Item as="li">
                                        <Link href={getLocalizedRoute(routes.serviceCentreSale, locale)} passHref>
                                            <Nav.Link active={isRoute(router.asPath, routes.serviceCentreSale, locale)} onClick={onHide}>
                                                <FormattedMessage defaultMessage="Prodej" />
                                            </Nav.Link>
                                        </Link>

                                        {locale !== LocaleEnum.sk && (
                                            <Header.NavDropdown>
                                                <SellMenu onHide={onHide} />
                                            </Header.NavDropdown>
                                        )}
                                    </Nav.Item>
                                </Nav>
                            </Header.NavDropdown>
                        </Nav.Item>

                        <Nav.Item as="li">
                            <Link href={getLocalizedRoute(routes.priceList, locale)} passHref>
                                <Nav.Link active={isRoute(router.asPath, routes.priceList, locale)}>
                                    <FormattedMessage defaultMessage="Ceník inzerce" />
                                </Nav.Link>
                            </Link>
                        </Nav.Item>
                    </Nav>
                </Header.Nav>

                <Header.Toolbar>
                    <ButtonGroup>
                        <Link
                            href={
                                user
                                    ? getLocalizedRoute(routes.watchdogList, locale)
                                    : getLocalizedRoute(routes.serviceCentreWatchdog, locale)
                            }
                            passHref
                            shallow
                        >
                            <Header.Button>
                                <VisuallyHidden>
                                    <FormattedMessage defaultMessage="Hlídací pes" />
                                </VisuallyHidden>
                                {user?.id && !!countData?.data?.user?.menuCounts?.watchdogs && (
                                    <ButtonBadge>
                                        <Badge>
                                            {countData.data.user.menuCounts?.watchdogs}
                                            <VisuallyHidden>
                                                <FormattedMessage defaultMessage="Upozornění" />
                                            </VisuallyHidden>
                                        </Badge>
                                    </ButtonBadge>
                                )}
                                <Icon.Watchdog position="middle" />
                            </Header.Button>
                        </Link>
                    </ButtonGroup>

                    <ButtonGroup>
                        <Link href={getLocalizedRoute(routes.favourites, locale)} passHref>
                            <Header.Button>
                                <VisuallyHidden>
                                    <FormattedMessage defaultMessage="Oblíbené" />
                                </VisuallyHidden>

                                {user?.id && !!countData?.data?.user?.menuCounts?.favourites && (
                                    <ButtonBadge>
                                        <Badge>
                                            {countData.data.user.menuCounts?.favourites}

                                            <VisuallyHidden>
                                                <FormattedMessage defaultMessage="označených" />
                                            </VisuallyHidden>
                                        </Badge>
                                    </ButtonBadge>
                                )}

                                <Icon.Heart position="middle" />
                            </Header.Button>
                        </Link>
                    </ButtonGroup>

                    <Header.Cta>
                        <Link href={getLocalizedRoute(routes.serviceCentreIntroOwner, locale)} passHref>
                            <Button size="sm" variant="secondary">
                                <Typography as="span" variant="cta">
                                    <FormattedMessage defaultMessage="Nabídnout nemovitost" />
                                </Typography>

                                <Icon.Key position="end" />
                            </Button>
                        </Link>
                    </Header.Cta>

                    <ButtonGroup className="d-md-none">{userMenuButton}</ButtonGroup>

                    <ButtonGroup className="d-none d-md-inline-flex">
                        {userMenuButton}

                        <Header.Button onClick={showMenuSidebar ? closeMenuSidebar : openMenuSidebar}>
                            <VisuallyHidden>
                                <FormattedMessage defaultMessage="Hlavní menu" />
                            </VisuallyHidden>
                            <Icon name={showMenuSidebar ? 'Close' : 'Menu'} position="middle" />
                        </Header.Button>
                    </ButtonGroup>
                </Header.Toolbar>
            </Header>

            {!!user ? (
                <SidebarMenuUser
                    show={showLoginSidebar}
                    onHide={closeLoginSidebar}
                    unreadMessageCount={countData.data?.user?.menuCounts?.conversations}
                />
            ) : (
                <SidebarLogin show={showLoginSidebar} onHide={closeLoginSidebar} />
            )}

            <SidebarMenu show={showMenuSidebar} onHide={closeMenuSidebar} />
        </>
    );
};
